import 'video.js/dist/video-js.css'

import { IconFont } from '@byecode/ui'
import type { VideoFitType } from '@lighthouse/core'
import { isWechatBrowser } from '@lighthouse/tools'
import cls from 'classnames'
import Hls, { type Level } from 'hls.js'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'

import { usePageActive } from '../../../contexts'
import { isWakeLockHeld, releaseScreenWakeLock, requestScreenWakeLock } from '../screenWakeLock'
import {
    applyInlineVideoAttributes,
    applySameLayerPlayAttributes,
    blockNativeFullscreen,
    createInlineFullscreenSession,
    exitSystemVideoFullscreen,
    getWechatVideoFullscreenMode,
    prefersStandardVideoFullscreen,
    readUserAgent,
    requestWechatVideoFullscreen,
    shouldUseInlineVideoFullscreen
} from '../wechatInlineFullscreen'

type QualityMode = 'auto' | 'manual'
export type MaxQuality = 'SD' | 'HD' | 'FHD' | 'UHD'
type QualityOption = {
    id: string
    label: string
    levelIndex: number
    bitrate?: number
    height?: number
    width?: number
    fps?: number
    codecs?: string
}
type QualityState = {
    enabled: boolean
    mode: QualityMode
    selectedLevel: number
    appliedLevel: number
    options: QualityOption[]
}
type QualityAction =
    | { type: 'SOURCE_RESET' }
    | { type: 'MANIFEST_READY'; options: QualityOption[] }
    | { type: 'USER_SELECT_AUTO' }
    | { type: 'USER_SELECT_LEVEL'; levelIndex: number }
    | { type: 'LEVEL_APPLIED'; levelIndex: number }

const AUTO_QUALITY_OPTION: QualityOption = { id: 'auto', label: '自动', levelIndex: -1 }
const QUALITY_MAX_HEIGHT: Record<MaxQuality, number> = {
    SD: 480,
    HD: 720,
    FHD: 1080,
    UHD: 2160
}
const INITIAL_QUALITY_STATE: QualityState = {
    enabled: false,
    mode: 'auto',
    selectedLevel: -1,
    appliedLevel: -1,
    options: []
}

const qualityReducer = (state: QualityState, action: QualityAction): QualityState => {
    switch (action.type) {
        case 'SOURCE_RESET':
            return INITIAL_QUALITY_STATE
        case 'MANIFEST_READY': {
            if (action.options.length <= 1) {
                return INITIAL_QUALITY_STATE
            }
            return {
                enabled: true,
                mode: 'auto',
                selectedLevel: -1,
                appliedLevel: -1,
                options: action.options
            }
        }
        case 'USER_SELECT_AUTO':
            return {
                ...state,
                mode: 'auto',
                selectedLevel: -1
            }
        case 'USER_SELECT_LEVEL':
            return {
                ...state,
                mode: 'manual',
                selectedLevel: action.levelIndex
            }
        case 'LEVEL_APPLIED':
            return {
                ...state,
                appliedLevel: action.levelIndex
            }
        default:
            return state
    }
}

const buildHlsQualityOptions = (levels: QualityOption[] = []): QualityOption[] => {
    if (levels.length === 0) {
        return [AUTO_QUALITY_OPTION]
    }

    const unique = new Map<string, QualityOption>()
    levels.forEach((level, index) => {
        const levelIndex = Number.isFinite(level.levelIndex) ? level.levelIndex : index
        const height = level.height
        const bitrate = level.bitrate
        // 某些流不会提供 height/bitrate，不能把不同 level 误合并成一个
        const key = height != null || bitrate != null ? `${height ?? 'na'}-${bitrate ?? 'na'}` : `level-${levelIndex}`
        if (unique.has(key)) {
            return
        }

        let label = `清晰度 ${levelIndex}`
        if (Number.isFinite(height) && (height ?? 0) > 0) {
            label = `${height}p`
        } else if (Number.isFinite(bitrate) && (bitrate ?? 0) > 0) {
            label = `${((bitrate ?? 0) / 1_000_000).toFixed(1)}Mbps`
        }

        unique.set(key, {
            ...level,
            id: `level-${levelIndex}`,
            label,
            levelIndex
        })
    })

    const levelOptions = [...unique.values()].sort((a, b) => {
        if ((b.height ?? 0) !== (a.height ?? 0)) {
            return (b.height ?? 0) - (a.height ?? 0)
        }
        return (b.bitrate ?? 0) - (a.bitrate ?? 0)
    })
    return [AUTO_QUALITY_OPTION, ...levelOptions]
}

const mapLevelsToOptions = (levels: Level[]): QualityOption[] =>
    buildHlsQualityOptions(
        levels.map((level, index) => ({
            id: `level-${index}`,
            label: '',
            levelIndex: index,
            bitrate: level.bitrate,
            height: level.height,
            width: level.width,
            fps: level.attrs?.['FRAME-RATE'] ? Number(level.attrs['FRAME-RATE']) : undefined,
            codecs: level.attrs?.CODECS
        }))
    )

export interface BasePlayerProps {
    source?: string
    sourceType?: 'mp4' | 'hls'
    isOrigin?: boolean
    ratio?: string
    /**
     * resumePlay 的存储 key，不传时退回用 source。
     * Byecode 场景下传 originalUrl，与签名后的 playUrl 解耦，避免续签换地址后进度 key 漂移。
     */
    progressKey?: string
    isMute?: boolean
    isController?: boolean
    isLoop?: boolean
    autoplay?: boolean
    resumePlay?: boolean
    fitType?: VideoFitType
    cover?: string
    showLoading?: boolean
    /** HLS 码率上限，未传则不限制 */
    maxQuality?: MaxQuality
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

const SCxVideoContainer = styled.div<{ ratio?: string }>`
    position: relative;
    width: 100%;
    height: auto;
    overflow: hidden;
    background-color: var(--color-black);
    aspect-ratio: ${({ ratio }) => ratio ?? 'auto'};

    .vjs-loading-spinner {
        display: none;
    }

    &.show-loading {
        display: block;

        .vjs-loading-spinner {
            display: none;
        }
    }

    .video-js {
        width: 100%;
        height: 100%;
        border-radius: inherit;
        background-color: var(--color-black);

        &.hide-quality-selector {
            .vjs-quality-selector {
                display: none;
            }
        }

        .vjs-big-play-button {
            display: none;
        }

        .vjs-picture-in-picture-control {
            display: none;
        }

        /* HLS 码率选择器 */
        .vjs-quality-selector {
            margin-right: 4px;

            .vjs-icon-placeholder {
                &::before {
                    font-size: 13px;
                    line-height: 30px;
                    font-weight: 600;
                    letter-spacing: 0.2px;
                    color: rgba(255, 255, 255, 0.95);
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
                    transition: color 0.2s ease;
                }
            }

            &:hover {
                .vjs-icon-placeholder::before {
                    color: #23ade5;
                }
            }

            .vjs-icon-placeholder::before {
                font-size: 13px;
                line-height: 30px;
            }

            .vjs-menu {
                width: 116px;
                border-radius: 10px;
                border: 1px solid rgba(255, 255, 255, 0.14);
                background: rgba(18, 20, 24, 0.94);
                backdrop-filter: blur(10px);
                box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
                overflow: hidden;

                .vjs-menu-content {
                    max-height: 240px;
                    padding: 6px 0;
                    overflow-y: auto;
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.32) transparent;

                    &::-webkit-scrollbar {
                        width: 5px;
                    }

                    &::-webkit-scrollbar-thumb {
                        border-radius: 999px;
                        background-color: rgba(255, 255, 255, 0.3);
                    }
                }

                .vjs-menu-title {
                    font-size: 0;
                    margin: 0;
                    height: 28px;
                    line-height: 28px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

                    &::after {
                        content: '清晰度';
                        display: block;
                        font-size: 11px;
                        font-weight: 600;
                        color: rgba(255, 255, 255, 0.52);
                        letter-spacing: 1px;
                        text-transform: uppercase;
                    }
                }

                .vjs-menu-item {
                    text-transform: uppercase;
                    height: 32px;
                    line-height: 32px;
                    margin: 2px 6px;
                    border-radius: 7px;
                    padding: 0 10px;
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.9);
                    transition: all 0.2s ease;

                    &:hover,
                    &:focus {
                        background-color: rgba(35, 173, 229, 0.18);
                        color: #5ec8f3;
                    }

                    &.vjs-selected {
                        position: relative;
                        background: linear-gradient(90deg, rgba(35, 173, 229, 0.24), rgba(35, 173, 229, 0.12));
                        color: #68d1fb;
                        font-weight: 600;

                        &::before {
                            content: '';
                            position: absolute;
                            left: 6px;
                            top: 50%;
                            width: 3px;
                            height: 14px;
                            border-radius: 999px;
                            background-color: #23ade5;
                            transform: translateY(-50%);
                        }
                    }
                }
            }
        }
    }
    &.fill {
        video {
            object-fit: cover;
        }

        .vjs-poster {
            img {
                object-fit: cover;
            }
        }
    }
`
const SCxOverLay = styled.div`
    position: absolute;
    z-index: 1;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`

const SCxPlayAndPause = styled.div`
    cursor: pointer;
`
const SCxIdleCover = styled.div<{ fitType?: string; visible?: boolean }>`
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: var(--color-black);
    background-position: center;
    background-repeat: no-repeat;
    background-size: ${({ fitType }) => (fitType === 'cover' ? 'cover' : 'contain')};
    opacity: ${({ visible = true }) => (visible ? 1 : 0)};
    pointer-events: none;
    transition: opacity ${({ visible = true }) => (visible ? '0s' : '0.18s')} ease;
`
const SCxPlayerLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
`

const SCxQualityWrap = styled.div`
    position: absolute;
    right: 12px;
    z-index: 3;
`

const SCxQualityButton = styled.button`
    border: 0;
    border-radius: 6px;
    padding: 4px 8px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.6);
    cursor: pointer;
`

const SCxQualityMenu = styled.div`
    position: absolute;
    right: 0;
    bottom: calc(100% + 6px);
    width: 116px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(18, 20, 24, 0.94);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
    overflow: hidden;
`

const SCxQualityItem = styled.button<{ selected: boolean }>`
    width: 100%;
    border: 0;
    height: 32px;
    line-height: 32px;
    padding: 0 10px;
    color: ${({ selected }) => (selected ? '#68d1fb' : 'rgba(255, 255, 255, 0.9)')};
    font-size: 12px;
    text-align: left;
    background: ${({ selected }) =>
        selected ? 'linear-gradient(90deg, rgba(35, 173, 229, 0.24), rgba(35, 173, 229, 0.12))' : 'transparent'};
    cursor: pointer;

    &:hover {
        color: #5ec8f3;
        background-color: rgba(35, 173, 229, 0.18);
    }
`
const MIME_TYPE: Record<'mp4' | 'hls', string> = {
    mp4: 'video/mp4',
    hls: 'application/x-mpegURL'
}
const VIDEO_VOLUME_STORAGE_KEY = 'video_player_volume'
const DEFAULT_VOLUME = 0.7
const GESTURE_THEN_PLAY_DELAY_MS = 120
const PLAYER_UNMOUNT_DELAY_MS = 160
type ActiveBasePlayerListener = (activeId: number | null) => void

let activeBasePlayerId: number | null = null
let basePlayerInstanceSeed = 0
const activeBasePlayerListeners = new Set<ActiveBasePlayerListener>()

const notifyActiveBasePlayerChange = () => {
    activeBasePlayerListeners.forEach(listener => {
        listener(activeBasePlayerId)
    })
}

const claimActiveBasePlayer = (instanceId: number) => {
    if (activeBasePlayerId === instanceId) {
        return
    }
    activeBasePlayerId = instanceId
    notifyActiveBasePlayerChange()
}

const releaseActiveBasePlayer = (instanceId: number) => {
    if (activeBasePlayerId !== instanceId) {
        return
    }
    activeBasePlayerId = null
    notifyActiveBasePlayerChange()
}

const subscribeActiveBasePlayer = (listener: ActiveBasePlayerListener) => {
    activeBasePlayerListeners.add(listener)
    return () => {
        activeBasePlayerListeners.delete(listener)
    }
}

const getStoredVolume = (): number => {
    const stored = localStorage.getItem(VIDEO_VOLUME_STORAGE_KEY)
    const volume = stored != null ? Number.parseFloat(stored) : Number.NaN
    if (!Number.isFinite(volume)) {
        return DEFAULT_VOLUME
    }
    return Math.min(Math.max(volume, 0), 1)
}

export const BasePlayer: React.FC<BasePlayerProps> = ({
    source,
    sourceType = 'mp4',
    progressKey,
    ratio,
    isMute = false,
    isController = true,
    isLoop = false,
    autoplay = false,
    resumePlay = false,
    fitType = 'auto',
    cover,
    showLoading = true,
    maxQuality
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const playerRef = useRef<Player | null>(null)
    const instanceIdRef = useRef<number | null>(null)
    if (instanceIdRef.current == null) {
        basePlayerInstanceSeed += 1
        instanceIdRef.current = basePlayerInstanceSeed
    }
    const instanceId = instanceIdRef.current
    const [isActive, setIsActive] = useState(false)
    const [isPlayerMounted, setIsPlayerMounted] = useState(false)
    const [isActivationCoverVisible, setIsActivationCoverVisible] = useState(false)
    const isPageActive = usePageActive()

    useEffect(() => {
        if (!isPageActive && playerRef.current) {
            playerRef.current.pause()
        }
    }, [isPageActive])
    const [played, setPlayed] = useState(false)
    const [loading, setLoading] = useState(showLoading)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [isControllerBar, setIsControllerBar] = useState(false)
    const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(autoplay)
    const [qualityState, setQualityState] = useState<QualityState>(INITIAL_QUALITY_STATE)
    const lastTimeRef = useRef(0)
    const restoredProgressRef = useRef(false)
    const hlsRef = useRef<Hls | null>(null)
    const suppressVolumePersistRef = useRef(false)
    const screenWakeLockRef = useRef<WakeLockSentinel | null>(null)
    const pendingPlayOnActivateRef = useRef(false)
    const hasEndedRef = useRef(false)
    const isActiveRef = useRef(isActive)
    isActiveRef.current = isActive
    const isPlayerMountedRef = useRef(isPlayerMounted)
    isPlayerMountedRef.current = isPlayerMounted
    const prevAutoplayRef = useRef(autoplay)
    const deactivateToCoverRef = useRef<((options?: { persistProgress?: boolean }) => void) | null>(null)
    const playerUnmountTimerRef = useRef<number | null>(null)

    // 用ref存储配置，避免闭包捕获旧值，同时避免依赖链传染
    const configRef = useRef({ resumePlay, showLoading, source, progressKey })
    configRef.current = { resumePlay, showLoading, source, progressKey }

    // sourceType 单独用 ref 跟踪，供 Effect 2 读取最新值
    const sourceTypeRef = useRef(sourceType)
    sourceTypeRef.current = sourceType
    const prevSourceTypeRef = useRef(sourceType)

    const maxQualityRef = useRef(maxQuality)
    maxQualityRef.current = maxQuality

    const filterLevelsByMaxQuality = (levels: Level[]): Level[] => {
        const currentMaxQuality = maxQualityRef.current
        if (!currentMaxQuality) {
            return levels
        }

        const maxHeight = QUALITY_MAX_HEIGHT[currentMaxQuality]
        return levels.filter(level => {
            const height = level.height
            if (!Number.isFinite(height) || (height ?? 0) <= 0) {
                return true
            }
            return (height ?? 0) <= maxHeight
        })
    }

    const destroyHls = useCallback(() => {
        hlsRef.current?.destroy()
        hlsRef.current = null
    }, [])

    const clearPlayerUnmountTimer = useCallback(() => {
        if (playerUnmountTimerRef.current !== null) {
            clearTimeout(playerUnmountTimerRef.current)
            playerUnmountTimerRef.current = null
        }
    }, [])

    const getProgressStorageKey = useCallback(() => {
        const { source, progressKey } = configRef.current
        return progressKey ?? source ? `video_progress_${progressKey ?? source}` : ''
    }, [])

    const persistCurrentProgress = useCallback(() => {
        if (hasEndedRef.current) {
            return
        }

        const player = playerRef.current
        const { resumePlay } = configRef.current
        const storageKey = getProgressStorageKey()
        if (!resumePlay || !storageKey || !player || player.isDisposed()) {
            return
        }

        const currentTime = player.currentTime() ?? 0
        if (!Number.isFinite(currentTime) || currentTime <= 0) {
            return
        }

        lastTimeRef.current = currentTime
        localStorage.setItem(storageKey, String(currentTime))
    }, [getProgressStorageKey])

    const resetPlaybackUiState = useCallback(
        ({ keepActivationCoverVisible = false }: { keepActivationCoverVisible?: boolean } = {}) => {
            if (!keepActivationCoverVisible) {
                setIsActivationCoverVisible(false)
            }
            setPlayed(false)
            setLoading(showLoading)
            setIsFullScreen(false)
            setIsControllerBar(false)
            setIsQualityMenuOpen(autoplay)
            setQualityState(INITIAL_QUALITY_STATE)
            lastTimeRef.current = 0
            restoredProgressRef.current = false
            suppressVolumePersistRef.current = false
            pendingPlayOnActivateRef.current = false
            hasEndedRef.current = false
        },
        [autoplay, showLoading]
    )

    const disposePlayer = useCallback(() => {
        destroyHls()
        releaseScreenWakeLock(screenWakeLockRef)
        const player = playerRef.current
        if (player && !player.isDisposed()) {
            player.dispose()
        }
        playerRef.current = null
    }, [destroyHls])

    const getTechVideoEl = useCallback((player: Player): HTMLVideoElement | null => {
        const techEl = player.el()?.querySelector('video')
        if (techEl instanceof HTMLVideoElement) {
            return techEl
        }
        return videoRef.current
    }, [])

    const applyHlsQuality = (levelIndex: number) => {
        const hls = hlsRef.current
        if (!hls) {
            return
        }
        if (levelIndex < 0) {
            // 恢复自适应：避免强制当前片段切换，交给 ABR 平滑接管
            hls.nextLevel = -1
            hls.loadLevel = -1
            setQualityState(prev => qualityReducer(prev, { type: 'USER_SELECT_AUTO' }))
            return
        }
        // 使用 nextLevel/loadLevel 让码率在后续分片生效，避免 currentLevel 触发瞬时重缓冲
        hls.nextLevel = levelIndex
        hls.loadLevel = levelIndex
        setQualityState(prev => qualityReducer(prev, { type: 'USER_SELECT_LEVEL', levelIndex }))
    }

    const syncSource = (nextSource?: string, nextSourceType: 'mp4' | 'hls' = sourceTypeRef.current) => {
        const player = playerRef.current
        const videoEl = player ? getTechVideoEl(player) : null
        if (!player || !videoEl || player.isDisposed()) {
            return
        }

        hasEndedRef.current = false
        setIsQualityMenuOpen(false)
        setQualityState(prev => qualityReducer(prev, { type: 'SOURCE_RESET' }))
        destroyHls()

        if (!nextSource) {
            player.reset()
            // reset() 会清空 cache_.playbackRates，不恢复则倍速菜单永久隐藏（见 Video.js resetCache_）
            player.playbackRates(PLAYBACK_RATES)
            return
        }

        if (nextSourceType === 'mp4') {
            player.src({ src: nextSource, type: MIME_TYPE.mp4 })
            return
        }

        if (!Hls.isSupported()) {
            player.src({ src: nextSource, type: MIME_TYPE.hls })
            return
        }

        const hls = new Hls()
        hlsRef.current = hls
        hls.attachMedia(videoEl)
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            hls.loadSource(nextSource)
        })
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            const filteredLevels = filterLevelsByMaxQuality(hls.levels)
            setQualityState(prev =>
                qualityReducer(prev, {
                    type: 'MANIFEST_READY',
                    options: mapLevelsToOptions(filteredLevels)
                })
            )
        })
        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
            setQualityState(prev => qualityReducer(prev, { type: 'LEVEL_APPLIED', levelIndex: data.level }))
        })
        hls.on(Hls.Events.ERROR, (_, data) => {
            if (!data.fatal) {
                return
            }
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                hls.startLoad()
                return
            }
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                hls.recoverMediaError()
                return
            }
            destroyHls()
        })
    }

    // 恢复播放进度 - 使用ref读取最新值，避免闭包问题
    const restoreProgress = () => {
        const player = playerRef.current
        if (!player || restoredProgressRef.current) {
            return
        }

        const { resumePlay } = configRef.current
        const storageKey = getProgressStorageKey()
        if (!resumePlay || !storageKey) {
            return
        }

        const savedTime = localStorage.getItem(storageKey)
        if (!savedTime) {
            return
        }

        const time = Number.parseFloat(savedTime)
        if (!Number.isFinite(time) || time <= 0) {
            restoredProgressRef.current = true
            return
        }

        // iOS/WebKit 对"metadata 未就绪就 seek"非常敏感：必须等到能拿到 duration 后再恢复进度
        const duration = player.duration()
        if (duration == null || !Number.isFinite(duration) || duration <= 0) {
            return
        }

        const safeTime = Math.min(Math.max(time, 0), Math.max(duration - 0.25, 0))
        try {
            player.currentTime(safeTime)
            restoredProgressRef.current = true
        } catch {
            // 某些浏览器/内核在特定状态下会抛异常，吞掉避免把播放器状态机炸掉
        }
    }

    const attemptPlaybackStart = useCallback(() => {
        if (!pendingPlayOnActivateRef.current) {
            return
        }

        window.setTimeout(() => {
            const player = playerRef.current
            if (!player || player.isDisposed()) {
                return
            }
            player.play()?.catch(() => {
                const techVideoEl = getTechVideoEl(player)
                techVideoEl?.play().catch(() => {
                    // keep silent: autoplay/gesture policy may still block in rare engines
                })
            })
        }, GESTURE_THEN_PLAY_DELAY_MS)
    }, [getTechVideoEl])

    /**
     * 与 Video.js 共用同一播放按钮：必须在同一轮点击之后读 player.paused()，
     * 否则 pause 事件可能已把 played 置 false，这里再误判为「要播放」去 request，叠加 touchend+click 双触发会打乱状态机（白屏）。
     */
    const handlePlayButtonGesture = useCallback(() => {
        window.queueMicrotask(() => {
            const p = playerRef.current
            if (!p || p.isDisposed()) {
                return
            }
            if (p.paused()) {
                releaseScreenWakeLock(screenWakeLockRef)
            } else {
                void requestScreenWakeLock(screenWakeLockRef)
            }
        })
    }, [])

    const deactivateToCover = useCallback(
        ({ persistProgress = true }: { persistProgress?: boolean } = {}) => {
            clearPlayerUnmountTimer()
            if (persistProgress) {
                persistCurrentProgress()
            }
            const player = playerRef.current
            player?.pause()
            releaseScreenWakeLock(screenWakeLockRef)
            releaseActiveBasePlayer(instanceId)
            setIsActivationCoverVisible(true)
            resetPlaybackUiState({ keepActivationCoverVisible: true })
            setIsActive(false)
            playerUnmountTimerRef.current = window.setTimeout(() => {
                playerUnmountTimerRef.current = null
                if (!isActiveRef.current) {
                    setIsPlayerMounted(false)
                }
            }, PLAYER_UNMOUNT_DELAY_MS)
        },
        [clearPlayerUnmountTimer, instanceId, persistCurrentProgress, resetPlaybackUiState]
    )
    deactivateToCoverRef.current = deactivateToCover

    const activatePlayer = useCallback(
        ({ playOnReady }: { playOnReady: boolean }) => {
            clearPlayerUnmountTimer()
            if (isActiveRef.current) {
                if (playOnReady) {
                    pendingPlayOnActivateRef.current = true
                    attemptPlaybackStart()
                }
                return
            }

            resetPlaybackUiState()
            setIsActivationCoverVisible(true)
            setIsPlayerMounted(true)
            pendingPlayOnActivateRef.current = playOnReady
            claimActiveBasePlayer(instanceId)
            setIsActive(true)
            if (playOnReady && isPlayerMountedRef.current && playerRef.current && !playerRef.current.isDisposed()) {
                attemptPlaybackStart()
            }
        },
        [attemptPlaybackStart, clearPlayerUnmountTimer, instanceId, resetPlaybackUiState]
    )

    useEffect(() => {
        const unsubscribe = subscribeActiveBasePlayer(activeId => {
            if (activeId !== instanceId && isActiveRef.current) {
                deactivateToCoverRef.current?.({ persistProgress: true })
            }
        })

        return unsubscribe
    }, [instanceId])

    // Effect 1: 激活后初始化播放器
    useEffect(() => {
        if (!isPlayerMounted || !videoRef.current || playerRef.current) {
            return
        }

        const userAgent = readUserAgent()
        const fullscreenMode = getWechatVideoFullscreenMode(userAgent)
        const desktopInline = shouldUseInlineVideoFullscreen(userAgent)
        if (fullscreenMode !== 'default') {
            if (desktopInline) {
                applyInlineVideoAttributes(videoRef.current)
            } else {
                applySameLayerPlayAttributes(videoRef.current)
            }
        }

        playerRef.current = videojs(videoRef.current, {
            autoplay,
            controls: isController,
            hidePictureInPicture: true,
            loop: isLoop,
            muted: autoplay,
            poster: cover,
            playsinline: true,
            volume: getStoredVolume(),
            preload: isWechatBrowser() ? 'none' : 'auto',
            playbackRates: PLAYBACK_RATES,
            sources: source && sourceType === 'mp4' ? [{ src: source, type: MIME_TYPE.mp4 }] : []
        })
        const player = playerRef.current

        // 绑定事件监听器 - 使用configRef读取最新配置
        player.on('play', async () => {
            hasEndedRef.current = false
            pendingPlayOnActivateRef.current = false
            setPlayed(true)
            suppressVolumePersistRef.current = true
            setIsControllerBar(true)
        })
        player.on('playing', () => {
            hasEndedRef.current = false
            setIsActivationCoverVisible(false)
            setLoading(false)
        })
        player.on('pause', async () => {
            releaseScreenWakeLock(screenWakeLockRef)
            setPlayed(false)
        })
        player.on('ended', () => {
            hasEndedRef.current = true
            releaseScreenWakeLock(screenWakeLockRef)
            setPlayed(false)
            const { resumePlay } = configRef.current
            const storageKey = getProgressStorageKey()
            if (resumePlay && storageKey) {
                localStorage.removeItem(storageKey)
            }
        })
        player.on('waiting', () => {
            if (configRef.current.showLoading) {
                setLoading(true)
            }
        })
        player.on('fullscreenchange', () => {
            setIsFullScreen(!!playerRef.current?.isFullscreen())
        })

        let detachWechatFullscreen = () => {}

        if (fullscreenMode !== 'default') {
            const inlineSession = createInlineFullscreenSession()
            const syncFullscreenToggle = () => {
                const toggle = player.getChild('controlBar')?.getChild('fullscreenToggle') as
                    | { handleFullscreenChange?: () => void }
                    | undefined
                toggle?.handleFullscreenChange?.()
            }

            const onExitFullWindow = () => {
                inlineSession.exit()
                syncFullscreenToggle()
                setIsFullScreen(false)
            }

            const showInlineWindow = () => {
                if (player.isDisposed() || !inlineSession.isActive()) {
                    return
                }
                player.enterFullWindow()
                syncFullscreenToggle()
                setIsFullScreen(true)
            }

            player.isFullscreen = () => {
                if (inlineSession.isActive() || player.isFullWindow) {
                    return true
                }
                const media = getTechVideoEl(player)
                if (media?.webkitDisplayingFullscreen) {
                    return true
                }
                return !!media && document.fullscreenElement === media
            }

            const onNativeFullscreenChange = () => {
                if (player.isDisposed()) {
                    return
                }
                const active = !!player.isFullscreen()
                if (!active) {
                    const media = getTechVideoEl(player)
                    if (media) {
                        exitSystemVideoFullscreen(media)
                    }
                }
                setIsFullScreen(active)
                syncFullscreenToggle()
            }
            if (prefersStandardVideoFullscreen(userAgent)) {
                document.addEventListener('fullscreenchange', onNativeFullscreenChange)
            }

            player.requestFullscreen = () => {
                if (player.isDisposed() || inlineSession.isActive()) {
                    return Promise.resolve()
                }
                const video = getTechVideoEl(player)
                const playerEl = player.el()
                const result = requestWechatVideoFullscreen({
                    userAgent,
                    video,
                    playerEl: playerEl instanceof HTMLElement ? playerEl : null,
                    inline: inlineSession,
                    onInline: showInlineWindow
                })
                if (result === 'inline') {
                    showInlineWindow()
                }
                if (result === 'system') {
                    setIsFullScreen(true)
                }
                return Promise.resolve()
            }
            player.exitFullscreen = () => {
                if (player.isDisposed()) {
                    return Promise.resolve()
                }
                const video = getTechVideoEl(player)
                if (video) {
                    exitSystemVideoFullscreen(video)
                }
                if (inlineSession.isActive()) {
                    player.exitFullWindow()
                } else {
                    setIsFullScreen(false)
                }
                return Promise.resolve()
            }
            player.on('exitFullWindow', onExitFullWindow)

            const redirectEnter = () => {
                void player.requestFullscreen()
            }
            const redirectExit = () => {
                void player.exitFullscreen()
            }
            const root = player.el()
            if (root instanceof HTMLElement) {
                blockNativeFullscreen(root, { enter: redirectEnter, exit: redirectExit })
            }
            const techVideo = getTechVideoEl(player)
            if (techVideo && desktopInline) {
                applyInlineVideoAttributes(techVideo)
                blockNativeFullscreen(techVideo, { enter: redirectEnter, exit: redirectExit })
            } else if (techVideo) {
                applySameLayerPlayAttributes(techVideo)
            }

            detachWechatFullscreen = () => {
                document.removeEventListener('fullscreenchange', onNativeFullscreenChange)
                player.off('exitFullWindow', onExitFullWindow)
                if (inlineSession.isActive() && !player.isDisposed() && player.isFullWindow) {
                    player.exitFullWindow()
                }
                inlineSession.exit()
            }
        }
        player.on('useractive', () => {
            suppressVolumePersistRef.current = true
            setIsControllerBar(true)
        })
        player.on('userinactive', () => {
            suppressVolumePersistRef.current = false
            setIsControllerBar(false)
        })
        player.on('canplay', () => setLoading(false))
        player.on('loadeddata', () => {
            setIsActivationCoverVisible(false)
        })
        player.on('canplay', () => {
            setIsActivationCoverVisible(false)
        })
        player.on('volumechange', () => {
            if (!suppressVolumePersistRef.current) {
                return
            }
            const currentVolume = playerRef.current?.volume()
            if (!Number.isFinite(currentVolume)) {
                return
            }
            localStorage.setItem(VIDEO_VOLUME_STORAGE_KEY, String(currentVolume))
        })

        // 定时保存播放进度
        player.on('timeupdate', () => {
            const currentTime = playerRef.current?.currentTime() ?? 0
            const { resumePlay } = configRef.current
            const storageKey = getProgressStorageKey()
            if (resumePlay && storageKey && Math.abs(currentTime - lastTimeRef.current) > 1) {
                lastTimeRef.current = currentTime
                localStorage.setItem(storageKey, currentTime.toString())
            }
        })

        // 恢复播放进度
        player.one('loadedmetadata', restoreProgress)
        player.one('canplay', restoreProgress)

        const bindControlPlayButton = () => {
            const controlButton = player.el()?.querySelector('.vjs-play-control')
            if (!(controlButton instanceof HTMLElement)) {
                return () => {}
            }
            controlButton.addEventListener('click', handlePlayButtonGesture)
            return () => {
                controlButton.removeEventListener('click', handlePlayButtonGesture)
            }
        }
        const unbindControlPlayButton = bindControlPlayButton()

        prevSourceRef.current = source
        prevSourceTypeRef.current = sourceType
        setIsActivationCoverVisible(true)
        syncSource(source, sourceType)
        attemptPlaybackStart()

        return () => {
            unbindControlPlayButton()
            detachWechatFullscreen()
            disposePlayer()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在进入激活态时初始化一次，运行时配置通过其他 effect 同步
    }, [isPlayerMounted])

    // Effect 2: source变化时重新加载视频（关键：只在source真正变化时才reload）
    const prevSourceRef = useRef(source)
    useEffect(() => {
        const player = playerRef.current
        if (!isActive || !player || player.isDisposed()) {
            return
        }

        // 跳过首次渲染（已在初始化时处理）
        if (prevSourceRef.current === source) {
            return
        }
        prevSourceRef.current = source

        // source变化，重置进度恢复状态
        restoredProgressRef.current = false
        lastTimeRef.current = 0

        setIsActivationCoverVisible(true)
        syncSource(source, sourceTypeRef.current)
        player.one('loadedmetadata', restoreProgress)
        player.one('canplay', restoreProgress)
        attemptPlaybackStart()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, source])

    useEffect(() => {
        const player = playerRef.current
        if (!isActive || !player || player.isDisposed()) {
            return
        }
        if (prevSourceTypeRef.current === sourceType) {
            return
        }
        prevSourceTypeRef.current = sourceType
        setIsActivationCoverVisible(true)
        syncSource(source, sourceType)
        player.one('loadedmetadata', restoreProgress)
        player.one('canplay', restoreProgress)
        attemptPlaybackStart()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, sourceType])

    useEffect(() => {
        const hls = hlsRef.current
        if (!hls) {
            return
        }
        const filteredLevels = filterLevelsByMaxQuality(hls.levels)
        setQualityState(prev =>
            qualityReducer(prev, {
                type: 'MANIFEST_READY',
                options: mapLevelsToOptions(filteredLevels)
            })
        )
    }, [maxQuality])

    // 仅保留码率状态机：当前先不变更现有 quality 插件 UI 行为
    useEffect(() => {
        if (!qualityState.enabled && qualityState.options.length === 0) {
            return
        }

        const hasSelected = qualityState.options.some(option => option.levelIndex === qualityState.selectedLevel)
        if (!hasSelected && qualityState.mode === 'manual') {
            setQualityState(prev => qualityReducer(prev, { type: 'USER_SELECT_AUTO' }))
        }
    }, [qualityState.enabled, qualityState.mode, qualityState.options, qualityState.selectedLevel, qualityState.appliedLevel])

    // Effect 3: 更新播放器配置（不触发reload，不消耗流量）
    useEffect(() => {
        const player = playerRef.current
        if (!player || player.isDisposed()) {
            return
        }

        player.controls(isController)
        if (!isController) {
            setIsControllerBar(false)
            setIsQualityMenuOpen(false)
        }
        player.muted(autoplay)
        player.volume(getStoredVolume())

        player.loop(isLoop)
        player.autoplay(autoplay)
        if (cover !== undefined) {
            player.poster(cover)
        }
    }, [isController, isLoop, autoplay, cover])

    useEffect(() => {
        const wasAutoplay = prevAutoplayRef.current
        if (autoplay && !isActiveRef.current) {
            activatePlayer({ playOnReady: true })
        } else if (!autoplay && wasAutoplay && isActiveRef.current) {
            deactivateToCover({ persistProgress: true })
        }
        prevAutoplayRef.current = autoplay
    }, [activatePlayer, autoplay, deactivateToCover])

    useEffect(() => {
        if (!isControllerBar && isQualityMenuOpen) {
            setIsQualityMenuOpen(false)
        }
    }, [isControllerBar, isQualityMenuOpen])

    useEffect(() => {
        return () => {
            clearPlayerUnmountTimer()
            if (isActiveRef.current) {
                persistCurrentProgress()
            }
            releaseActiveBasePlayer(instanceId)
            if (isPlayerMountedRef.current) {
                disposePlayer()
            }
        }
    }, [clearPlayerUnmountTimer, disposePlayer, instanceId, persistCurrentProgress])

    const playEle = useMemo(
        () => (
            <SCxOverLay
                onClick={async () => {
                    const player = playerRef.current
                    if (!isActive) {
                        if (!isWakeLockHeld(screenWakeLockRef)) {
                            await requestScreenWakeLock(screenWakeLockRef)
                        }
                        activatePlayer({ playOnReady: true })
                        return
                    }
                    if (!player) {
                        return
                    }
                    if (!isWakeLockHeld(screenWakeLockRef)) {
                        await requestScreenWakeLock(screenWakeLockRef)
                    }
                    window.setTimeout(() => {
                        player.play()?.catch(() => {
                            const techVideoEl = getTechVideoEl(player)
                            techVideoEl?.play().catch(() => {
                                // keep silent: autoplay/gesture policy may still block in rare engines
                            })
                        })
                    }, GESTURE_THEN_PLAY_DELAY_MS)
                }}
                style={{
                    bottom: isActive && isControllerBar ? 30 : 0
                }}
            >
                <SCxPlayAndPause>
                    <IconFont size={48} color="var(--color-gray-200)" type="BlockVideo" />
                </SCxPlayAndPause>
            </SCxOverLay>
        ),
        [activatePlayer, getTechVideoEl, isActive, isControllerBar]
    )

    const selectedQualityLabel = useMemo(() => {
        if (!qualityState.enabled) {
            return '清晰度'
        }
        if (qualityState.mode === 'auto') {
            const applied = qualityState.options.find(option => option.levelIndex === qualityState.appliedLevel)
            return applied ? `自动(${applied.label})` : '自动'
        }
        return qualityState.options.find(option => option.levelIndex === qualityState.selectedLevel)?.label ?? '自动'
    }, [qualityState])

    const qualityMenuEle = useMemo(() => {
        if (!(isController && qualityState.enabled && isControllerBar)) {
            return null
        }
        return (
            <SCxQualityWrap style={{ bottom: isFullScreen ? 52 : 42 }}>
                <SCxQualityButton type="button" onClick={() => setIsQualityMenuOpen(prev => !prev)}>
                    {selectedQualityLabel}
                </SCxQualityButton>
                {isQualityMenuOpen && (
                    <SCxQualityMenu onMouseLeave={() => setIsQualityMenuOpen(false)}>
                        {qualityState.options.map(option => {
                            const selected =
                                (qualityState.mode === 'auto' && option.levelIndex < 0) ||
                                (qualityState.mode === 'manual' && option.levelIndex === qualityState.selectedLevel)
                            return (
                                <SCxQualityItem
                                    key={option.id}
                                    type="button"
                                    selected={selected}
                                    onClick={() => {
                                        applyHlsQuality(option.levelIndex)
                                        setIsQualityMenuOpen(false)
                                    }}
                                >
                                    {option.label}
                                </SCxQualityItem>
                            )
                        })}
                    </SCxQualityMenu>
                )}
            </SCxQualityWrap>
        )
    }, [isController, qualityState, isControllerBar, selectedQualityLabel, isQualityMenuOpen, isFullScreen])

    const fullscreenPortalTarget = (() => {
        if (!isFullScreen) {
            return null
        }
        const player = playerRef.current
        if (!player || player.isDisposed()) {
            return null
        }
        const el = player.el()
        return el instanceof HTMLElement ? el : null
    })()

    return (
        <SCxVideoContainer
            className={cls({ fill: fitType === 'cover', 'show-loading': isActive && loading, 'video-root': true })}
            ratio={ratio}
            ref={containerRef}
        >
            <SCxIdleCover
                fitType={fitType}
                style={cover ? { backgroundImage: `url(${cover})` } : undefined}
                visible={!isActive || isActivationCoverVisible}
            />
            {isPlayerMounted && (
                <SCxPlayerLayer>
                    <video ref={videoRef} className="video-js vjs-default-skin" />
                </SCxPlayerLayer>
            )}
            {qualityMenuEle && (fullscreenPortalTarget ? createPortal(qualityMenuEle, fullscreenPortalTarget) : qualityMenuEle)}
            {!played && (fullscreenPortalTarget ? createPortal(playEle, fullscreenPortalTarget) : playEle)}
        </SCxVideoContainer>
    )
}
